import { FC, useEffect, useMemo, useState } from "react"
import { ChevronDown, User, X } from "lucide-react"
import { useSearchLoanOfficerQuery } from "../../../../redux/queries"
import { convertLoToAutocompleteOption } from "../../../utils"

interface Props {
  orgId: string
  selectedLoanOfficer: string | null
  onChange: (loid: string | null) => void
}

const LoanOfficerFilter: FC<Props> = ({
  orgId,
  selectedLoanOfficer,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")

  // Debounce search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [query])

  // Search for loan officers
  const { data: loanOfficers, isFetching } = useSearchLoanOfficerQuery({
    org_id: orgId,
    fullname: debouncedQuery,
    limit: "20"
  })

  // Fetch selected LO details if needed
  const { data: selectedLoData } = useSearchLoanOfficerQuery(
    { loid: selectedLoanOfficer ?? "" },
    { skip: !selectedLoanOfficer }
  )

  const options = useMemo(() => {
    return loanOfficers?.map(convertLoToAutocompleteOption) ?? []
  }, [loanOfficers])

  const selectedLoName = useMemo(() => {
    if (!selectedLoanOfficer) return null
    const fromList = options.find(opt => opt.id === selectedLoanOfficer)
    if (fromList) return fromList.text
    if (selectedLoData?.[0]) return selectedLoData[0].name
    return null
  }, [selectedLoanOfficer, options, selectedLoData])

  const handleSelect = (loid: string | null) => {
    onChange(loid)
    setIsOpen(false)
    setQuery("")
  }

  return (
    <div className="relative">
      {/* Filter Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
          selectedLoanOfficer
            ? "bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100"
            : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
        }`}
      >
        <User className="w-4 h-4" />
        <span className="text-sm font-medium truncate max-w-[150px]">
          {selectedLoName ?? "All Loan Officers"}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Clear Button */}
      {selectedLoanOfficer && (
        <button
          onClick={() => handleSelect(null)}
          className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 text-sm transition-colors duration-200"
        >
          <X className="w-3.5 h-3.5" />
          Clear
        </button>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false)
              setQuery("")
            }}
          />

          {/* Menu */}
          <div className="absolute z-20 mt-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden animate-[fadeSlideIn_0.2s_ease-out_forwards]">
            <div className="p-3 border-b border-slate-200 bg-slate-50">
              <input
                type="text"
                placeholder="Search loan officers..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {/* All Loan Officers option */}
              <button
                onClick={() => handleSelect(null)}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 transition-colors duration-150 border-b border-slate-100 ${
                  !selectedLoanOfficer
                    ? "bg-primary-50 text-primary-700 font-medium"
                    : "text-slate-700"
                }`}
              >
                All Loan Officers
              </button>

              {/* Loading state */}
              {isFetching && (
                <div className="px-4 py-3 text-sm text-slate-500">
                  Loading...
                </div>
              )}

              {/* Loan officer options */}
              {!isFetching &&
                options.map(option => (
                  <button
                    key={option.id}
                    onClick={() => handleSelect(option.id)}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-50 transition-colors duration-150 ${
                      selectedLoanOfficer === option.id
                        ? "bg-primary-50 text-primary-700 font-medium"
                        : "text-slate-700"
                    }`}
                  >
                    {option.text}
                  </button>
                ))}

              {/* Empty state */}
              {!isFetching && options.length === 0 && query && (
                <div className="px-4 py-3 text-sm text-slate-500">
                  No loan officers found
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default LoanOfficerFilter
